// Shared @react-pdf building blocks — port of the reportlab layout used for the
// approved mock packets (docs/forms/GAPP_packet_pdf_template.py.txt).
// One source of truth: these components render BOTH the on-screen preview and the
// final filed PDF, so what parents see and what we file can never drift.

import React from 'react';
import {
  Document, Page, View, Text, Image, StyleSheet, Font,
} from '@react-pdf/renderer';
// Company letterhead logo, served as a static asset. @react-pdf decodes a data
// URI via Buffer (absent in CRA/webpack-5 browsers), so we load it by URL — the
// same approach used for the signature font below.
// Navy-on-transparent square mark (no baked-in subtitle — the name/tagline are
// already separate Text elements below) — ahfc-logo.png itself is cream-on-
// transparent, meant for the dark navy login hero, and would be invisible on
// white paper.
const LOGO_SRC = `${(typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || ''}/ahfc-logo-navy-square.png`;

// ── Palette (matches reportlab mock) ─────────────────────────────────────────
export const COLORS = {
  navy:   '#0A1628',
  steel:  '#1A3A6B',
  light:  '#EAF0F8',
  green:  '#0DA06A',
  grey:   '#F1EDE3',
  border: '#B9CBE0',
  mid:    '#6B7FA3',
  sign:   '#000000',   // signatures are black, never green or blue (decided 2026-09-20)
  ink:    '#1a3a6b',
};

// Stylized cursive for typed signatures. Registers a Google-hosted handwriting
// font; falls back to Helvetica-Oblique if the network fetch fails at render.
let cursiveRegistered = false;
export function ensureCursiveFont() {
  if (cursiveRegistered) return;
  try {
    const base = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '';
    Font.register({
      family: 'Signature',
      src: `${base}/fonts/DancingScript.ttf`,
    });
    cursiveRegistered = true;
  } catch (_) { /* fallback handled in styles */ }
}

export const styles = StyleSheet.create({
  page: {
    paddingTop: 43, paddingBottom: 43, paddingHorizontal: 54,
    fontFamily: 'Helvetica', fontSize: 9, color: '#000', lineHeight: 1.4,
  },
  // form title — light heading with a steel accent rule (no heavy fill)
  bandWrap: { marginBottom: 10, marginTop: 2, borderBottomWidth: 2, borderBottomColor: COLORS.steel, paddingBottom: 5 },
  bandTitle: { fontFamily: 'Helvetica-Bold', fontSize: 14, color: COLORS.navy },
  bandSub: { fontSize: 8.5, color: COLORS.mid, marginTop: 2 },
  // per-page letterhead (fixed on every page)
  bandLogo: { width: 30, height: 30 },

  sectionHdr: { fontFamily: 'Helvetica-Bold', fontSize: 9, color: COLORS.navy, marginTop: 10, marginBottom: 4 },
  formSub: { fontFamily: 'Helvetica-Bold', fontSize: 10, color: COLORS.steel, marginTop: 10, marginBottom: 6 },

  body: { fontSize: 9, marginBottom: 3, lineHeight: 1.4 },
  bodyJust: { fontSize: 9, marginBottom: 3, lineHeight: 1.4, textAlign: 'justify' },
  legal: { fontSize: 8, color: '#333', marginBottom: 2, lineHeight: 1.45, textAlign: 'justify' },
  bullet: { fontSize: 9, marginBottom: 2, lineHeight: 1.4, paddingLeft: 12 },

  // field rows
  fieldRow: { flexDirection: 'row', alignItems: 'flex-end', marginBottom: 5 },
  fieldLabel: { fontFamily: 'Helvetica-Bold', fontSize: 7, color: COLORS.mid },
  fieldValueWrap: { borderBottomWidth: 0.5, borderBottomColor: COLORS.border, flexGrow: 1, paddingBottom: 2, marginLeft: 6 },
  fieldValue: { fontFamily: 'Helvetica-Bold', fontSize: 9, color: COLORS.ink },
  fieldValueEmpty: { fontSize: 9, color: '#bbb' },

  // signature blocks
  sigGrid: { marginTop: 8 },
  sigLabel: { fontFamily: 'Helvetica-Bold', fontSize: 7, color: COLORS.mid, marginTop: 12 },
  sigLine: { borderTopWidth: 0.8, borderTopColor: '#000', marginTop: 1 },
  signature: { fontFamily: 'Signature', fontSize: 13, color: COLORS.sign, lineHeight: 1 },
  sigName: { fontSize: 8, color: '#000', marginTop: 1 },

  stamp: { fontFamily: 'Helvetica-Bold', fontSize: 8, color: COLORS.ink, textAlign: 'right', marginTop: 6 },

  // checks
  checkRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 3 },
  checkMark: { fontFamily: 'Helvetica-Bold', fontSize: 9, color: COLORS.green, width: 14 },
  checkBox: { fontSize: 9, width: 14 },
  checkLabel: { fontSize: 9, flexGrow: 1 },

  // letterhead header (matches the .docx letterhead: logo + name + tagline + navy rule)
  letterhead:        { flexDirection: 'row', alignItems: 'flex-end', borderBottomWidth: 1.5, borderBottomColor: '#1A3A5C', paddingBottom: 5, marginBottom: 16 },
  letterheadLogo:    { width: 32, height: 48, objectFit: 'contain', marginRight: 10 },
  letterheadTextWrap:{ flexGrow: 1, alignItems: 'flex-end' },
  letterheadName:    { color: '#1A3A5C', fontFamily: 'Helvetica-Bold', fontSize: 17, lineHeight: 1.1 },
  letterheadTagline: { color: '#6B7280', fontFamily: 'Helvetica-Oblique', fontSize: 9, marginTop: 2 },
  coverTitle: { fontFamily: 'Helvetica-Bold', fontSize: 18, color: COLORS.navy, textAlign: 'center', marginBottom: 4 },
  coverSub: { fontSize: 11, color: COLORS.mid, textAlign: 'center', marginBottom: 10 },
  coverInfoRow: { flexDirection: 'row', borderBottomWidth: 0.3, borderBottomColor: COLORS.border, paddingVertical: 5 },
  coverInfoLabel: { fontFamily: 'Helvetica-Bold', fontSize: 9, color: COLORS.mid, width: '22%' },
  coverInfoVal: { fontSize: 9, width: '28%' },
});

// ── helpers ───────────────────────────────────────────────────────────────────

// Form header: title + subtitle on the left, AHFC logo on the right, over a rule.
export const Band = ({ title, subtitle }) => (
  <View style={styles.bandWrap}>
    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
      <View style={{ flex: 1, paddingRight: 10 }}>
        <Text style={styles.bandTitle}>{title}</Text>
        {subtitle ? <Text style={styles.bandSub}>{subtitle}</Text> : null}
      </View>
      <Image style={styles.bandLogo} src={LOGO_SRC} />
    </View>
  </View>
);

// Content page (no top letterhead strip — branding lives in the form header).
export const PacketPage = ({ children }) => (
  <Page size="LETTER" style={styles.page}>
    {children}
  </Page>
);

export const Field = ({ label, value }) => (
  <View style={styles.fieldRow}>
    <Text style={[styles.fieldLabel, { width: 95 }]}>{label}</Text>
    <View style={styles.fieldValueWrap}>
      {value
        ? <Text style={styles.fieldValue}>{value}</Text>
        : <Text style={styles.fieldValueEmpty}>{' '}</Text>}
    </View>
  </View>
);

// two fields side by side
export const Field2 = ({ pairs }) => (
  <View style={{ flexDirection: 'row', marginBottom: 5 }}>
    {pairs.map(([label, value], i) => (
      <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-end', width: '50%', paddingRight: i === 0 ? 12 : 0 }}>
        <Text style={[styles.fieldLabel, { width: 95 }]}>{label}</Text>
        <View style={styles.fieldValueWrap}>
          {value ? <Text style={styles.fieldValue}>{value}</Text> : <Text style={styles.fieldValueEmpty}>{' '}</Text>}
        </View>
      </View>
    ))}
  </View>
);

export const Check = ({ checked, label }) => (
  <View style={styles.checkRow}>
    {checked ? <Text style={styles.checkMark}>{'✓'}</Text> : <Text style={styles.checkBox}>{'☐'}</Text>}
    <Text style={styles.checkLabel}>{label}</Text>
  </View>
);

// signers: [{ label, name, date }]
export const SigBlock = ({ signers }) => (
  <View style={styles.sigGrid}>
    {signers.map((s, i) => (
      <View key={i} style={{ marginBottom: 10 }}>
        <View style={{ flexDirection: 'row' }}>
          <Text style={[styles.sigLabel, { flexGrow: 1 }]}>{s.label}</Text>
          <Text style={[styles.sigLabel, { width: 110 }]}>Date</Text>
        </View>
        <View style={{ flexDirection: 'row' }}>
          <View style={{ flexGrow: 1, marginRight: 14 }}>
            <Text style={styles.signature}>{s.name || ' '}</Text>
            <View style={styles.sigLine} />
            <Text style={styles.sigName}>{s.name || ''}</Text>
          </View>
          <View style={{ width: 110 }}>
            <Text style={styles.signature}>{s.date || ' '}</Text>
            <View style={styles.sigLine} />
          </View>
        </View>
      </View>
    ))}
  </View>
);

// Simple table. headers: [str]; rows: [[cell,...]]; widths: [%/pt strings] optional.
export const DataTable = ({ headers, rows, widths }) => {
  const n = headers.length;
  const w = widths || headers.map(() => `${100 / n}%`);
  return (
    <View style={{ marginBottom: 6, marginTop: 4 }}>
      <View style={{ flexDirection: 'row', backgroundColor: COLORS.navy }}>
        {headers.map((h, i) => (
          <Text key={i} style={{ width: w[i], color: '#fff', fontFamily: 'Helvetica-Bold', fontSize: 8, padding: 4 }}>{h}</Text>
        ))}
      </View>
      {rows.map((row, ri) => (
        <View key={ri} style={{ flexDirection: 'row', backgroundColor: ri % 2 === 0 ? COLORS.grey : '#fff' }}>
          {row.map((cell, ci) => (
            <Text key={ci} style={{ width: w[ci], fontSize: 8, padding: 4, borderWidth: 0.3, borderColor: COLORS.border }}>{cell}</Text>
          ))}
        </View>
      ))}
    </View>
  );
};

export const Stamp = ({ date }) => (
  <Text style={styles.stamp}>{`✓ ELECTRONICALLY SIGNED — CAERA — ${date || ''}`}</Text>
);

// Company letterhead header — logo + name + tagline above a navy rule.
// Mirrors the .docx letterhead so generated Word docs and signing packets match.
export const Letterhead = () => (
  <View style={styles.letterhead}>
    <Image style={styles.letterheadLogo} src={LOGO_SRC} />
    <View style={styles.letterheadTextWrap}>
      <Text style={styles.letterheadName}>A Heart For Care</Text>
      <Text style={styles.letterheadTagline}>Home Care Services by Hard Shoulder Group LLC</Text>
    </View>
  </View>
);

export const Cover = ({ title, subtitle, info, forms, date }) => (
  <View>
    <View style={{ height: 30 }} />
    <Letterhead />
    <Text style={styles.coverTitle}>{title}</Text>
    {subtitle ? <Text style={styles.coverSub}>{subtitle}</Text> : null}
    <View style={{ borderBottomWidth: 1.5, borderBottomColor: COLORS.steel, marginBottom: 12 }} />
    {info.map((row, i) => (
      <View key={i} style={[styles.coverInfoRow, { backgroundColor: i % 2 === 0 ? COLORS.grey : '#fff' }]}>
        <Text style={styles.coverInfoLabel}>{row[0]}</Text>
        <Text style={styles.coverInfoVal}>{row[1]}</Text>
        <Text style={styles.coverInfoLabel}>{row[2]}</Text>
        <Text style={styles.coverInfoVal}>{row[3]}</Text>
      </View>
    ))}
    <View style={{ height: 14 }} />
    <Text style={styles.formSub}>FORMS INCLUDED IN THIS PACKET</Text>
    {forms.map((f, i) => (
      <View key={i} style={{ backgroundColor: i % 2 === 0 ? COLORS.light : '#fff', paddingVertical: 5, paddingHorizontal: 10, borderBottomWidth: 0.3, borderBottomColor: COLORS.border }}>
        <Text style={{ fontSize: 8, color: COLORS.navy }}>{f}</Text>
      </View>
    ))}
    <View style={{ height: 16 }} />
    <Text style={{ fontSize: 8, color: COLORS.mid, textAlign: 'center', fontFamily: 'Helvetica-Oblique' }}>
      This document was completed and signed electronically via CAERA. All signatures are
      legally valid under the ESIGN Act and UETA.
    </Text>
  </View>
);

export { Document, Page, View, Text };
